import type { TurnPhaseTiming } from '@atbash-labs/ontology-agent-wire'

/**
 * Readers for timing as it appears on a persisted tool call. The harness
 * writes camelCase; the snake_case fallbacks cover records that round-tripped
 * through the proxy's JSON surfaces and custom-agent invocation payloads.
 * All fields are absent on records written before timing existed.
 */

function finiteMs(raw: unknown): number | undefined {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined
}

function nonNegativeMs(raw: unknown): number | undefined {
  const ms = finiteMs(raw)
  return ms !== undefined && ms >= 0 ? ms : undefined
}

export function readCallDurationMs(rec: Record<string, unknown>): number | null {
  return nonNegativeMs(rec.durationMs ?? rec.duration_ms) ?? null
}

export function readCallStartedAt(rec: Record<string, unknown>): number | undefined {
  return nonNegativeMs(rec.startedAt ?? rec.started_at)
}

/**
 * Phases are nested under `turnTiming` on harness records and flattened onto
 * the payload for custom-agent invocations, so both shapes are accepted.
 * Negative values pass through for `deriveTurnPhases` to clamp — one odd
 * number should not suppress the whole breakdown.
 */
export function readCallTurnTiming(rec: Record<string, unknown>): TurnPhaseTiming | undefined {
  const nested = rec.turnTiming ?? rec.turn_timing
  const source =
    nested !== null && typeof nested === 'object' ? (nested as Record<string, unknown>) : rec
  const waitingMs = finiteMs(source.waitingMs ?? source.waiting_ms)
  const writingMs = finiteMs(source.writingMs ?? source.writing_ms)
  if (waitingMs === undefined || writingMs === undefined) return undefined
  return { waitingMs, writingMs }
}
